import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-13c45a5e"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["width", "height"]
const _hoisted_2 = ["transform"]
const _hoisted_3 = ["x1", "x2", "y2"]
const _hoisted_4 = ["x"]
const _hoisted_5 = ["width", "height"]
const _hoisted_6 = ["d", "fill", "fill-opacity", "onClick"]
const _hoisted_7 = ["x", "y"]
const _hoisted_8 = ["x", "y"]
const _hoisted_9 = ["x", "y"]

import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { stack, stackOffsetWiggle, area, curveMonotoneX } from 'd3-shape'
import { scaleLinear } from 'd3-scale'
import { extent } from 'd3-array'
import type { NamePeriodCount } from './NameCounts'
import { PersonNamePart } from '@/rd/ResearchDataModel'

const chartHeight = 300
const minWidth = 768


export default /*#__PURE__*/_defineComponent({
  __name: 'NameStreamgraph',
  props: {
    data: {},
    namePart: {}
  },
  setup(__props: any) {

const props = __props;



const router = useRouter()

const colors = [
  '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
  '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
  '#6a9bc3', '#f5a960', '#e8807f', '#93ccc7', '#7ab86e',
  '#f2d974', '#c49aba', '#ffb5bf', '#b59279', '#cdc4be',
  '#3a6d94', '#d97a1e', '#c9403f', '#5a9e98', '#44843a',
]

const margin = { top: 24, right: 16, bottom: 8, left: 16 }
const wrapperRef = ref<HTMLDivElement>()
const containerWidth = ref(600)
const selectedName = ref<string>()

const width = computed(() => Math.max(containerWidth.value, minWidth))
const height = chartHeight
const innerWidth = computed(() => width.value - margin.left - margin.right)
const innerHeight = computed(() => height - margin.top - margin.bottom)

const top10Names = computed(() => {
  const totals = new Map<string, number>()
  for (const d of props.data) {
    totals.set(d.name, (totals.get(d.name) ?? 0) + d.count)
  }
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 25)
    .map(([name]) => name)
    .sort((a, b) => a.localeCompare(b))
})

const periods = computed(() => {
  const set = new Set<number>()
  for (const d of props.data) set.add(d.period)
  return [...set].sort((a, b) => a - b)
})

const stackedData = computed(() => {
  if (periods.value.length === 0 || top10Names.value.length === 0) return []

  const names = top10Names.value
  const lookup = new Map<string, Map<number, number>>()
  for (const d of props.data) {
    if (!names.includes(d.name)) continue
    let periodMap = lookup.get(d.name)
    if (!periodMap) { periodMap = new Map(); lookup.set(d.name, periodMap) }
    periodMap.set(d.period, d.count)
  }

  const rows = periods.value.map(period => {
    const row: Record<string, number> & { period: number } = { period }
    for (const name of names) {
      row[name] = lookup.get(name)?.get(period) ?? 0
    }
    return row
  })

  const stackGen = stack<typeof rows[0]>()
    .keys(names)
    .offset(stackOffsetWiggle)

  return stackGen(rows)
})

const midpoints = computed(() => periods.value.map(p => p + 25))

const xScale = computed(() => {
  if (midpoints.value.length === 0) return scaleLinear().domain([0, 1]).range([0, innerWidth.value])
  return scaleLinear()
    .domain([midpoints.value[0], midpoints.value[midpoints.value.length - 1]])
    .range([innerWidth.value, 0])
})

const yScale = computed(() => {
  if (stackedData.value.length === 0) return scaleLinear().domain([-1, 1]).range([innerHeight.value, 0])
  const all = stackedData.value.flatMap(layer => layer.flatMap(d => [d[0], d[1]]))
  const [lo, hi] = extent(all) as [number, number]
  return scaleLinear().domain([lo, hi]).range([innerHeight.value, 0])
})

const areaGen = computed(() => {
  const xS = xScale.value
  const yS = yScale.value
  return area<[number, number] & { data: { period: number } }>()
    .x(d => xS(d.data.period + 25))
    .y0(d => yS(d[0]))
    .y1(d => yS(d[1]))
    .curve(curveMonotoneX)
})

const streamLayers = computed(() => {
  return stackedData.value.map((layer, i) => ({
    name: layer.key,
    path: areaGen.value(layer as any) ?? '',
    color: colors[i % colors.length],
  }))
})

const nameLabels = computed(() => {
  const xS = xScale.value
  const yS = yScale.value
  return stackedData.value.map((layer, i) => {
    let bestIdx = 0
    let bestHeight = 0
    for (let j = 0; j < layer.length; j++) {
      const h = Math.abs(yS(layer[j][0]) - yS(layer[j][1]))
      if (h > bestHeight) { bestHeight = h; bestIdx = j }
    }
    const d = layer[bestIdx]
    const period = periods.value[bestIdx]
    const x = xS(period + 25)
    const y = (yS(d[0]) + yS(d[1])) / 2
    const count = layer[bestIdx].data[layer.key] as number
    return { name: layer.key, x, y, count, visible: bestHeight > 14, color: colors[i % colors.length] }
  }).filter(l => l.visible)
})

const selectedPeriodCounts = computed(() => {
  if (!selectedName.value) return []
  const xS = xScale.value
  const yS = yScale.value
  const layer = stackedData.value.find(l => l.key === selectedName.value)
  if (!layer) return []
  const labelIdx = nameLabels.value.find(l => l.name === selectedName.value) ? findBestIdx(layer) : -1
  const result: { name: string, period: number, count: number, x: number, y: number }[] = []
  for (let j = 0; j < layer.length; j++) {
    if (j === labelIdx) continue
    const d = layer[j]
    const count = d.data[layer.key] as number
    if (count === 0) continue
    const bandHeight = Math.abs(yS(d[0]) - yS(d[1]))
    if (bandHeight < 12) continue
    result.push({
      name: layer.key,
      period: periods.value[j],
      count,
      x: xS(periods.value[j] + 25),
      y: (yS(d[0]) + yS(d[1])) / 2,
    })
  }
  return result
})

function findBestIdx(layer: typeof stackedData.value[0]) {
  const yS = yScale.value
  let bestIdx = 0
  let bestHeight = 0
  for (let j = 0; j < layer.length; j++) {
    const h = Math.abs(yS(layer[j][0]) - yS(layer[j][1]))
    if (h > bestHeight) { bestHeight = h; bestIdx = j }
  }
  return bestIdx
}

const periodBoundaries = computed(() => {
  if (periods.value.length === 0) return []
  const xS = xScale.value
  const ps = periods.value
  const boundaries = ps.map(p => ({ period: p, x: xS(p) }))
  boundaries.push({ period: ps[ps.length - 1] + 50, x: xS(ps[ps.length - 1] + 50) })
  return boundaries
})

function onLayerClick(name: string) {
  if (selectedName.value === name) {
    router.push({ name: 'namesearch', params: { part: props.namePart, name } })
  } else {
    selectedName.value = name
  }
}

let resizeObserver: ResizeObserver | undefined

onMounted(() => {
  if (!wrapperRef.value) return
  containerWidth.value = wrapperRef.value.clientWidth
  resizeObserver = new ResizeObserver(entries => {
    containerWidth.value = entries[0].contentRect.width
  })
  resizeObserver.observe(wrapperRef.value)
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "streamgraph-wrapper",
    ref_key: "wrapperRef",
    ref: wrapperRef
  }, [
    (_openBlock(), _createElementBlock("svg", {
      width: width.value,
      height: _unref(height)
    }, [
      _createElementVNode("g", {
        transform: `translate(${margin.left},${margin.top})`
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(periodBoundaries.value, (boundary) => {
          return (_openBlock(), _createElementBlock("g", {
            key: boundary.period
          }, [
            _createElementVNode("line", {
              x1: boundary.x,
              y1: 0,
              x2: boundary.x,
              y2: innerHeight.value,
              stroke: "#ccc",
              "stroke-width": "1"
            }, null, 8, _hoisted_3),
            _createElementVNode("text", {
              x: boundary.x,
              y: -6,
              "font-size": "10",
              fill: "#888",
              "text-anchor": "middle"
            }, _toDisplayString(boundary.period), 9, _hoisted_4)
          ]))
        }), 128)),
        _createElementVNode("rect", {
          width: innerWidth.value,
          height: innerHeight.value,
          fill: "transparent",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (selectedName.value = undefined))
        }, null, 8, _hoisted_5),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(streamLayers.value, (layer) => {
          return (_openBlock(), _createElementBlock("path", {
            key: layer.name,
            d: layer.path,
            fill: layer.color,
            "fill-opacity": selectedName.value && selectedName.value !== layer.name ? 0.3 : 0.8,
            style: {"cursor":"pointer"},
            onClick: _withModifiers(($event: any) => (onLayerClick(layer.name)), ["stop"])
          }, null, 8, _hoisted_6))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nameLabels.value, (label) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: label.name
          }, [
            (selectedName.value === label.name)
              ? (_openBlock(), _createElementBlock("text", {
                  key: 0,
                  x: label.x,
                  y: label.y,
                  "font-size": "11",
                  fill: "white",
                  "font-weight": "normal",
                  "text-anchor": "middle",
                  "dominant-baseline": "middle",
                  "pointer-events": "none"
                }, _toDisplayString(label.name) + " (" + _toDisplayString(label.count) + ")", 9, _hoisted_7))
              : (_openBlock(), _createElementBlock("text", {
                  key: 1,
                  x: label.x,
                  y: label.y,
                  "font-size": "11",
                  fill: "white",
                  "font-weight": "normal",
                  "text-anchor": "middle",
                  "dominant-baseline": "middle",
                  "pointer-events": "none"
                }, _toDisplayString(label.name), 9, _hoisted_8))
          ], 64))
        }), 128)),
        (selectedName.value)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(selectedPeriodCounts.value, (pc) => {
              return (_openBlock(), _createElementBlock("text", {
                key: `${pc.name}-${pc.period}`,
                x: pc.x,
                y: pc.y,
                "font-size": "9",
                fill: "white",
                "fill-opacity": "0.9",
                "text-anchor": "middle",
                "dominant-baseline": "middle",
                "pointer-events": "none"
              }, _toDisplayString(pc.count), 9, _hoisted_9))
            }), 128))
          : _createCommentVNode("", true)
      ], 8, _hoisted_2)
    ], 8, _hoisted_1))
  ], 512))
}
}

})